import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import BrandLogo from "@/components/BrandLogo";
import { InstagramIcon, LinkedinIcon, YoutubeIcon, FacebookIcon } from "@/components/SocialIcons";
import { HEADER } from "@/constants/testIds";
import { COURSE_CATEGORIES } from "@/data/courses";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";

const TEACHABLE_ALL_URL = "https://zarantech.teachable.com/l/products";

// Video Course tab's dropdown categories -- all point straight at a
// Teachable URL (an external platform, via `url`), unlike the "Courses"
// mega menu's internal /courses?category= routes.
const VIDEO_COURSE_CATEGORIES = [
  { id: "sap", title: "SAP", url: "https://zarantech.teachable.com/l/products?courseCategory=SAP" },
  { id: "workday", title: "Workday", url: "https://zarantech.teachable.com/courses/category/workday" },
  { id: "ai", title: "AI", url: "https://zarantech.teachable.com/courses/category/ai" },
  { id: "microsoft", title: "Microsoft", url: "https://zarantech.teachable.com/l/products?courseCategory=Microsoft" },
  { id: "bundles", title: "Bundles (Video Courses)", url: "https://zarantech.teachable.com/l/products?sortKey=name&sortDirection=asc&page=1&productType=product_collection&sa=off" },
];

const NAV = [
  { to: "/", label: "Home", testid: HEADER.navHome },
  { to: "/courses", label: "Courses", testid: HEADER.navCourses, hasMega: true },
  {
    to: TEACHABLE_ALL_URL,
    label: "Video Course",
    testid: HEADER.navVideoCourse,
    external: true,
    hasVideoMega: true,
  },
  { to: "/corporate-training", label: "Corporate Training", testid: HEADER.navCorporate, hasCorporateMega: true },
  { to: "/leadership", label: "AI for Leadership", testid: HEADER.navLeadership },
  { to: "/graduate-training", label: "Graduate Training" },
  { to: "/resources", label: "Resources", testid: HEADER.navResources, hasResourcesMega: true },
  // Hidden from nav (routes/code retained): Live Sessions (still reachable via
  // the Resources dropdown), Blog and Refer a Friend.
  // { to: "/live-sessions", label: "Live Sessions" },
  // { to: "/blog", label: "Blog" },
  // { to: "/refer-a-friend", label: "Refer a Friend" },
  { to: "/contact", label: "Contact Us", testid: HEADER.navContact, isCta: true },
];

// Rendered separately from the rest of NAV on desktop -- pinned to the far
// right instead of sitting inside the centered nav list (see below). The
// mobile menu still renders it inline via the plain NAV.map further down.
const CTA_NAV_ITEM = NAV.find((item) => item.isCta);

// Tiny copy line per catalog category ,  kept here (not in data) so the data file stays clean.
const MEGA_BLURBS = {
  sap: "SAP S/4HANA, Public Cloud, SAP AI, Joule and enterprise SAP solutions.",
  workday: "Workday HCM, Financials, Integrations, Reporting, Prism and Extend.",
  ai: "ChatGPT, Claude, Prompt Engineering, AI Agents and Enterprise AI.",
  microsoft: "Microsoft Copilot, Power BI, Power Platform and Azure AI.",
  ai_leaders: "Executive AI, AI Strategy, Governance and Digital Transformation.",
  bundles: "End-to-end paths bundling multiple courses.",
};

// Resources dropdown ,  each links to an existing top-level page (Blog is
// otherwise hidden from the nav, so this dropdown is the only way to it).
const RESOURCES_LINKS = [
  { to: "/resources", title: "Guides/EBooks", blurb: "Downloadable guides, eBooks and certification Q&A." },
  { to: "/live-sessions", title: "Live Sessions", blurb: "Upcoming live sessions and on-demand masterclass replays." },
  { to: "/blog", title: "Blogs", blurb: "Practical articles on SAP, Workday, AI and enterprise skills." },
];

// Corporate Training tracks ,  each has its own page and audience.
const CORPORATE_TRACKS = [
  { to: "/corporate-training/sap", title: "SAP for Corporate", blurb: "S/4HANA, SuccessFactors, SAP AI and enterprise SAP skilling for teams." },
  { to: "/corporate-training/workday", title: "Workday for Corporate", blurb: "HCM, Financials, Integrations and Reporting for Workday teams." },
  { to: "/corporate-training/ai", title: "AI for Corporate", blurb: "Practical AI, prompt engineering and AI agents for the wider workforce." },
  { to: "/corporate-training/ai-leadership", title: "AI for Leadership", blurb: "AI strategy, governance and adoption for executives and leaders." },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [videoMegaOpen, setVideoMegaOpen] = useState(false);
  const [corporateMegaOpen, setCorporateMegaOpen] = useState(false);
  const [resourcesMegaOpen, setResourcesMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Hides the floating WhatsApp widget (a third-party Elfsight embed from
  // index.html) while the mobile nav sheet is open, so it doesn't sit on
  // top of the menu, then restores it once the sheet closes. Elfsight's
  // script doesn't necessarily render inside the placeholder div we gave it
  // in index.html -- it can mount its actual floating button as a new
  // top-level element elsewhere in <body> -- so this scans broadly for any
  // Elfsight/eapps element instead of assuming one fixed selector, and
  // re-scans on DOM mutations while the sheet is open to also catch the
  // widget if it loads (or reloads) after the menu was already opened.
  // Scoped to mobile only (matches the sheet's own lg breakpoint).
  useEffect(() => {
    document.body.classList.toggle("mobile-nav-open", mobileOpen);
    if (!mobileOpen) {
      document.querySelectorAll('[class*="elfsight" i], [id*="elfsight" i], [class*="eapps" i], [id*="eapps" i]')
        .forEach((el) => { el.style.removeProperty("display"); });
      return undefined;
    }

    const hideIfMobile = () => {
      if (window.innerWidth >= 1024) return;
      document.querySelectorAll('[class*="elfsight" i], [id*="elfsight" i], [class*="eapps" i], [id*="eapps" i]')
        .forEach((el) => { el.style.setProperty("display", "none", "important"); });
    };
    hideIfMobile();

    const observer = new MutationObserver(hideIfMobile);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", hideIfMobile);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", hideIfMobile);
      document.body.classList.remove("mobile-nav-open");
      document.querySelectorAll('[class*="elfsight" i], [id*="elfsight" i], [class*="eapps" i], [id*="eapps" i]')
        .forEach((el) => { el.style.removeProperty("display"); });
    };
  }, [mobileOpen]);

  const handleEnquire = () => {
    trackEvent(ANALYTICS_EVENTS.CTA_CLICKED, { id: "header-enquire" });
    navigate("/contact#enquire");
  };
  const handleExplore = () => {
    trackEvent(ANALYTICS_EVENTS.CTA_CLICKED, { id: "header-explore" });
    navigate("/courses");
  };

  return (
    <header
      data-testid={HEADER.root}
      className={`fixed inset-x-0 top-0 z-50 transition-all ${scrolled
          ? "bg-white border-b border-line shadow-md"
          : "bg-white border-b border-line"
        }`}
    >
      <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
        <div className="flex h-16 lg:h-20 items-center justify-between gap-6">
          <BrandLogo />

          {/* Desktop nav -- centered in the space between the logo and the
              Contact Us button (rendered separately below, pinned to the
              far right), so neither of those two ever moves. */}
          <nav className="hidden lg:flex flex-1 items-center justify-center gap-1">
            {NAV.filter((item) => !item.isCta).map((item) =>
              item.hasMega ? (
                <div
                  key={item.to}
                  className="relative"
                  onMouseEnter={() => setMegaOpen(true)}
                  onMouseLeave={() => setMegaOpen(false)}
                >
                  <NavLink
                    to={item.to}
                    data-testid={HEADER.navCoursesTrigger}
                    onClick={() => setMegaOpen(false)}
                    className={({ isActive }) =>
                      `relative inline-flex items-center gap-1 px-2 py-2 text-sm font-medium whitespace-nowrap transition-colors ${isActive ? "text-royal-deep" : "text-ink hover:text-royal"
                      }`
                    }
                  >
                    {item.label}
                    <ChevronDown size={14} className={`transition-transform ${megaOpen ? "rotate-180" : ""}`} />
                  </NavLink>

                  {megaOpen && (
                    <div className="absolute left-1/2 top-full -translate-x-1/2 pt-3">
                      <div className="w-[680px] rounded-2xl border border-line bg-white shadow-[0_30px_80px_-30px_rgba(15,42,115,0.35)] p-6">
                        <div className="mb-4 flex items-center justify-between">
                          <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-ink-soft">
                            Browse training categories
                          </p>
                          <Link
                            to="/courses"
                            onClick={() => setMegaOpen(false)}
                            className="text-xs font-semibold text-royal hover:text-royal-deep inline-flex items-center gap-1"
                          >
                            All courses <ArrowRight size={12} />
                          </Link>
                        </div>
                        <div className="grid grid-cols-2 gap-1">
                          {COURSE_CATEGORIES.filter((c) => c.id !== "bundles").map((c) => (
                            <Link
                              key={c.id}
                              to={`/courses?category=${c.id}`}
                              data-testid={`mega-cat-${c.id}`}
                              className="group rounded-xl px-4 py-3 hover:bg-[var(--cyan-tint)] transition-colors"
                              onClick={() => {
                                setMegaOpen(false);
                                trackEvent(ANALYTICS_EVENTS.CATEGORY_SELECTED, { from: "mega", id: c.id });
                              }}
                            >
                              <div className="flex items-start justify-between">
                                <span className="font-display font-semibold text-ink group-hover:text-royal-deep">
                                  {c.title}
                                </span>
                                <ArrowRight
                                  size={14}
                                  className="text-ink-soft opacity-0 group-hover:opacity-100 group-hover:text-royal transition"
                                />
                              </div>
                              <p className="mt-1 text-xs text-ink-soft leading-relaxed">
                                {MEGA_BLURBS[c.id] || ""}
                              </p>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

              ) : item.hasVideoMega ? (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => setVideoMegaOpen(true)}
                  onMouseLeave={() => setVideoMegaOpen(false)}
                >
                  <a
                    href={item.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={HEADER.navVideoCourseTrigger}
                    onClick={() => setVideoMegaOpen(false)}
                    className="relative inline-flex items-center gap-1 px-2 py-2 text-sm font-medium text-ink hover:text-royal transition-colors whitespace-nowrap"
                  >
                    {item.label}
                    <ChevronDown size={14} className={`transition-transform ${videoMegaOpen ? "rotate-180" : ""}`} />
                  </a>

                  {videoMegaOpen && (
                    <div className="absolute left-1/2 top-full -translate-x-1/2 pt-3">
                      <div className="w-[680px] rounded-2xl border border-line bg-white shadow-[0_30px_80px_-30px_rgba(15,42,115,0.35)] p-6">
                        <div className="mb-4 flex items-center justify-between">
                          <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-ink-soft">
                            Browse video course categories
                          </p>
                          <a
                            href={TEACHABLE_ALL_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setVideoMegaOpen(false)}
                            className="text-xs font-semibold text-royal hover:text-royal-deep inline-flex items-center gap-1"
                          >
                            All courses <ArrowRight size={12} />
                          </a>
                        </div>
                        <div className="grid grid-cols-2 gap-1">
                          {VIDEO_COURSE_CATEGORIES.map((c) => {
                            const content = (
                              <>
                                <div className="flex items-start justify-between">
                                  <span className="font-display font-semibold text-ink group-hover:text-royal-deep">
                                    {c.title}
                                  </span>
                                  <ArrowRight
                                    size={14}
                                    className="text-ink-soft opacity-0 group-hover:opacity-100 group-hover:text-royal transition"
                                  />
                                </div>
                                <p className="mt-1 text-xs text-ink-soft leading-relaxed">
                                  {MEGA_BLURBS[c.id] || ""}
                                </p>
                              </>
                            );
                            const sharedProps = {
                              key: c.id,
                              "data-testid": `video-mega-cat-${c.id}`,
                              className: "group rounded-xl px-4 py-3 hover:bg-[var(--cyan-tint)] transition-colors",
                              onClick: () => {
                                setVideoMegaOpen(false);
                                trackEvent(ANALYTICS_EVENTS.CATEGORY_SELECTED, { from: "video-mega", id: c.id });
                              },
                            };
                            return c.to ? (
                              <Link {...sharedProps} to={c.to}>{content}</Link>
                            ) : (
                              <a {...sharedProps} href={c.url} target="_blank" rel="noopener noreferrer">{content}</a>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

              ) : item.hasCorporateMega ? (
                <div
                  key={item.to}
                  className="relative"
                  onMouseEnter={() => setCorporateMegaOpen(true)}
                  onMouseLeave={() => setCorporateMegaOpen(false)}
                >
                  <NavLink
                    to={item.to}
                    data-testid={item.testid}
                    onClick={() => setCorporateMegaOpen(false)}
                    className={({ isActive }) =>
                      `relative inline-flex items-center gap-1 px-2 py-2 text-sm font-medium whitespace-nowrap transition-colors ${isActive ? "text-royal-deep" : "text-ink hover:text-royal"
                      }`
                    }
                  >
                    {item.label}
                    <ChevronDown size={14} className={`transition-transform ${corporateMegaOpen ? "rotate-180" : ""}`} />
                  </NavLink>

                  {corporateMegaOpen && (
                    <div className="absolute left-1/2 top-full -translate-x-1/2 pt-3">
                      <div className="w-[560px] rounded-2xl border border-line bg-white shadow-[0_30px_80px_-30px_rgba(15,42,115,0.35)] p-6">
                        <div className="mb-4">
                          <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-ink-soft">
                            Corporate training tracks
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-1">
                          {CORPORATE_TRACKS.map((c) => (
                            <Link
                              key={c.to}
                              to={c.to}
                              className="group rounded-xl px-4 py-3 hover:bg-[var(--cyan-tint)] transition-colors"
                              onClick={() => setCorporateMegaOpen(false)}
                            >
                              <div className="flex items-start justify-between">
                                <span className="font-display font-semibold text-ink group-hover:text-royal-deep">
                                  {c.title}
                                </span>
                                <ArrowRight
                                  size={14}
                                  className="text-ink-soft opacity-0 group-hover:opacity-100 group-hover:text-royal transition"
                                />
                              </div>
                              <p className="mt-1 text-xs text-ink-soft leading-relaxed">
                                {c.blurb}
                              </p>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

              ) : item.hasResourcesMega ? (
                <div
                  key={item.to}
                  className="relative"
                  onMouseEnter={() => setResourcesMegaOpen(true)}
                  onMouseLeave={() => setResourcesMegaOpen(false)}
                >
                  <button
                    type="button"
                    data-testid={item.testid}
                    onClick={() => setResourcesMegaOpen((o) => !o)}
                    className="relative inline-flex items-center gap-1 px-2 py-2 text-sm font-medium text-ink hover:text-royal whitespace-nowrap transition-colors"
                  >
                    {item.label}
                    <ChevronDown size={14} className={`transition-transform ${resourcesMegaOpen ? "rotate-180" : ""}`} />
                  </button>

                  {resourcesMegaOpen && (
                    <div className="absolute left-1/2 top-full -translate-x-1/2 pt-3">
                      <div className="w-[320px] rounded-2xl border border-line bg-white shadow-[0_30px_80px_-30px_rgba(15,42,115,0.35)] p-4">
                        {RESOURCES_LINKS.map((r) => (
                          <Link
                            key={r.to}
                            to={r.to}
                            className="group block rounded-xl px-4 py-3 hover:bg-[var(--cyan-tint)] transition-colors"
                            onClick={() => setResourcesMegaOpen(false)}
                          >
                            <div className="flex items-start justify-between">
                              <span className="font-display font-semibold text-ink group-hover:text-royal-deep">
                                {r.title}
                              </span>
                              <ArrowRight
                                size={14}
                                className="text-ink-soft opacity-0 group-hover:opacity-100 group-hover:text-royal transition"
                              />
                            </div>
                            <p className="mt-1 text-xs text-ink-soft leading-relaxed">
                              {r.blurb}
                            </p>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

              ) : item.external ? (
                <a
                  key={item.label}
                  href={item.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-2 py-2 text-sm font-medium text-ink hover:text-royal whitespace-nowrap transition-colors"
                >
                  {item.label}
                </a>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `px-2 py-2 text-sm font-medium whitespace-nowrap transition-colors ${isActive ? "text-royal-deep" : "text-ink hover:text-royal"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          {/* Contact Us -- pinned to the far right, outside the centered
              nav, so it never drifts inward and always sits flush with the
              container's edge. */}
          {CTA_NAV_ITEM && (
            <NavLink
              to={CTA_NAV_ITEM.to}
              data-testid={CTA_NAV_ITEM.testid}
              className="hidden lg:inline-flex items-center gap-2 rounded-md bg-royal-deep px-4 py-2.5 text-sm font-semibold text-white whitespace-nowrap transition-all hover:bg-royal hover:shadow-[0_12px_24px_-12px_rgba(15,42,115,0.6)]"
            >
              {CTA_NAV_ITEM.label} <ArrowRight size={14} />
            </NavLink>
          )}

          {/* Mobile */}
          <div className="flex lg:hidden items-center gap-2">
            <button
              type="button"
              data-testid={HEADER.ctaExplore + "-mobile"}
              onClick={handleExplore}
              className="rounded-md bg-royal-deep px-4 py-2 text-xs font-semibold text-white"
            >
              Explore
            </button>
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  data-testid={HEADER.mobileToggle}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-line text-ink"
                  aria-label="Open menu"
                >
                  {mobileOpen ? <X size={18} /> : <Menu size={18} />}
                </button>
              </SheetTrigger>
              <SheetContent side="right" data-testid={HEADER.mobileMenu} className="w-[88vw] max-w-sm">
                <SheetTitle className="sr-only">Site navigation</SheetTitle>
                <SheetDescription className="sr-only">Browse pages, course categories and primary calls to action.</SheetDescription>
                <div className="mt-4 flex flex-col gap-1">
                  {NAV.map((item) => {
                    // Each of these mirrors its desktop mega-dropdown as a
                    // tap-to-expand accordion instead, since mobile has no
                    // hover state.
                    if (item.hasMega) {
                      return (
                        <details key={item.to} className="mobile-nav-accordion">
                          <summary
                            data-testid={item.testid + "-mobile"}
                            className="flex items-center justify-between gap-2 rounded-lg px-3 py-3 text-base font-medium text-ink hover:bg-bg-alt"
                          >
                            {item.label}
                            <ChevronDown size={16} className="text-ink-soft shrink-0" />
                          </summary>
                          <div className="pl-3 pb-2 flex flex-col gap-1">
                            <Link
                              to="/courses"
                              onClick={() => setMobileOpen(false)}
                              className="rounded-lg px-3 py-2 text-sm font-semibold text-royal-deep hover:bg-bg-alt"
                            >
                              All courses
                            </Link>
                            {COURSE_CATEGORIES.map((c) => (
                              <Link
                                key={c.id}
                                to={`/courses?category=${c.id}`}
                                onClick={() => setMobileOpen(false)}
                                className="rounded-lg px-3 py-2 text-sm text-ink hover:bg-bg-alt"
                              >
                                {c.title}
                              </Link>
                            ))}
                          </div>
                        </details>
                      );
                    }

                    if (item.hasVideoMega) {
                      return (
                        <details key={item.to} className="mobile-nav-accordion">
                          <summary
                            data-testid={HEADER.navVideoCourseTrigger + "-mobile"}
                            className="flex items-center justify-between gap-2 rounded-lg px-3 py-3 text-base font-medium text-ink hover:bg-bg-alt"
                          >
                            {item.label}
                            <ChevronDown size={16} className="text-ink-soft shrink-0" />
                          </summary>
                          <div className="pl-3 pb-2 flex flex-col gap-1">
                            <a
                              href={TEACHABLE_ALL_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => setMobileOpen(false)}
                              className="rounded-lg px-3 py-2 text-sm font-semibold text-royal-deep hover:bg-bg-alt"
                            >
                              All courses
                            </a>
                            {VIDEO_COURSE_CATEGORIES.map((c) =>
                              c.to ? (
                                <Link
                                  key={`video-${c.id}`}
                                  to={c.to}
                                  onClick={() => setMobileOpen(false)}
                                  className="rounded-lg px-3 py-2 text-sm text-ink hover:bg-bg-alt"
                                >
                                  {c.title}
                                </Link>
                              ) : (
                                <a
                                  key={`video-${c.id}`}
                                  href={c.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => setMobileOpen(false)}
                                  className="rounded-lg px-3 py-2 text-sm text-ink hover:bg-bg-alt"
                                >
                                  {c.title}
                                </a>
                              )
                            )}
                          </div>
                        </details>
                      );
                    }

                    if (item.hasCorporateMega) {
                      return (
                        <details key={item.to} className="mobile-nav-accordion">
                          <summary
                            data-testid={item.testid + "-mobile"}
                            className="flex items-center justify-between gap-2 rounded-lg px-3 py-3 text-base font-medium text-ink hover:bg-bg-alt"
                          >
                            {item.label}
                            <ChevronDown size={16} className="text-ink-soft shrink-0" />
                          </summary>
                          <div className="pl-3 pb-2 flex flex-col gap-1">
                            <Link
                              to={item.to}
                              onClick={() => setMobileOpen(false)}
                              className="rounded-lg px-3 py-2 text-sm font-semibold text-royal-deep hover:bg-bg-alt"
                            >
                              Corporate Training overview
                            </Link>
                            {CORPORATE_TRACKS.map((c) => (
                              <Link
                                key={c.to}
                                to={c.to}
                                onClick={() => setMobileOpen(false)}
                                className="rounded-lg px-3 py-2 text-sm text-ink hover:bg-bg-alt"
                              >
                                {c.title}
                              </Link>
                            ))}
                          </div>
                        </details>
                      );
                    }

                    if (item.hasResourcesMega) {
                      return (
                        <details key={item.to} className="mobile-nav-accordion">
                          <summary
                            data-testid={item.testid + "-mobile"}
                            className="flex items-center justify-between gap-2 rounded-lg px-3 py-3 text-base font-medium text-ink hover:bg-bg-alt"
                          >
                            {item.label}
                            <ChevronDown size={16} className="text-ink-soft shrink-0" />
                          </summary>
                          <div className="pl-3 pb-2 flex flex-col gap-1">
                            {RESOURCES_LINKS.map((r) => (
                              <Link
                                key={r.to}
                                to={r.to}
                                onClick={() => setMobileOpen(false)}
                                className="rounded-lg px-3 py-2 text-sm text-ink hover:bg-bg-alt"
                              >
                                {r.title}
                              </Link>
                            ))}
                          </div>
                        </details>
                      );
                    }

                    if (item.external) {
                      return (
                        <a
                          key={item.to}
                          href={item.to}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setMobileOpen(false)}
                          className="rounded-lg px-3 py-3 text-base font-medium text-ink hover:bg-bg-alt"
                        >
                          {item.label}
                        </a>
                      );
                    }

                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        data-testid={item.testid + "-mobile"}
                        onClick={() => setMobileOpen(false)}
                        className={({ isActive }) =>
                          `rounded-lg px-3 py-3 text-base font-medium ${isActive ? "bg-bg-alt text-royal-deep" : "text-ink hover:bg-bg-alt"
                          }`
                        }
                      >
                        {item.label}
                      </NavLink>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => {
                      setMobileOpen(false);
                      handleExplore();
                    }}
                    className="mt-4 rounded-md bg-royal-deep px-4 py-3 text-sm font-semibold text-white"
                  >
                    Explore Courses
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMobileOpen(false);
                      handleEnquire();
                    }}
                    className="rounded-md border border-royal-deep px-4 py-3 text-sm font-semibold text-royal-deep"
                  >
                    Enquire Now
                  </button>
                  <div className="my-3 h-px bg-line" />
                  <p className="px-3 pb-2 font-mono-ui text-[11px] uppercase tracking-[0.2em] text-ink-soft">
                    Get Involved
                  </p>
                  <Link
                    to="/careers"
                    onClick={() => setMobileOpen(false)}
                    className="rounded-lg px-3 py-2 text-sm text-ink hover:bg-bg-alt"
                  >
                    Career Page
                  </Link>
                  <Link
                    to="/become-a-trainer"
                    onClick={() => setMobileOpen(false)}
                    className="rounded-lg px-3 py-2 text-sm text-ink hover:bg-bg-alt"
                  >
                    Become a Trainer
                  </Link>
                  <Link
                    to="/refer-a-friend"
                    onClick={() => setMobileOpen(false)}
                    className="rounded-lg px-3 py-2 text-sm text-ink hover:bg-bg-alt"
                  >
                    Refer a Friend
                  </Link>
                  <div className="my-3 h-px bg-line" />
                  <p className="px-3 pb-2 font-mono-ui text-[11px] uppercase tracking-[0.2em] text-ink-soft">
                    Follow us
                  </p>
                  <div className="flex items-center gap-5 px-3">
                    <a
                      href="https://www.instagram.com/zarantech/"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      className="hover:opacity-80 transition-opacity"
                    >
                      <InstagramIcon brand className="h-6 w-6" />
                    </a>
                    <a
                      href="https://www.linkedin.com/company/zarantech-llc"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="LinkedIn"
                      className="hover:opacity-80 transition-opacity"
                    >
                      <LinkedinIcon brand className="h-6 w-6" />
                    </a>
                    <a
                      href="https://www.youtube.com/@zarantechdotcom"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="YouTube"
                      className="hover:opacity-80 transition-opacity"
                    >
                      <YoutubeIcon brand className="h-6 w-6" />
                    </a>
                    <a
                      href="https://www.facebook.com/ZaranTech/"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Facebook"
                      className="hover:opacity-80 transition-opacity"
                    >
                      <FacebookIcon brand className="h-6 w-6" />
                    </a>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
